import { Trash2 } from "lucide-react";
import React from "react";

import {
  IgIconButton,
  IgLink,
  IgPaper,
  IgSelect,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { UrlMatch, UrlMatchList } from "@intelligems/ig-types";
import { Alert, Grid, MenuItem, useTheme } from "@mui/material";
import { getTargetPreviewDisplayValue } from "@src/components/targeting/utils/utils";
import { openNewTab } from "@src/utils/openNewTab";

interface CustomEventUrlProps {
  url: { matchBy: UrlMatch; value: string };
  index: number;
  isArchived: boolean;
  onMatchChange: (index: number, newValue: UrlMatch) => void;
  onUrlChange: (index: number, newValue: string) => void;
  onRemove: (index: number) => void;
  domain: string;
  error?: string | null;
}

const TargetPreview = ({
  previewDisplayValue,
  domain,
  url,
}: {
  previewDisplayValue: string;
  domain: string;
  url: { matchBy: UrlMatch; value: string };
}) => {
  return (
    <Grid item marginTop={"10px"}>
      <IgTypography fontWeight={500}>Target Preview: </IgTypography>
      <IgTypography>
        {previewDisplayValue}{" "}
        {!url.value.trim() &&
        (url.matchBy === "equals" || url.matchBy === "doesNotEqual")
          ? "(homepage)"
          : null}
      </IgTypography>
    </Grid>
  );
};

export const CustomEventUrl = ({
  url,
  index,
  isArchived,
  onMatchChange,
  onUrlChange,
  onRemove,
  domain,
  error,
}: CustomEventUrlProps) => {
  const theme = useTheme();
  const urlLabels: Record<UrlMatch, string> = {
    contains: "contains",
    equals: "equals",
    startsWith: "starts with",
    endsWith: "ends with",
    doesNotEqual: "does not equal",
    doesNotContain: "does not contain",
    doesNotStartWith: "does not start with",
    doesNotEndWith: "does not end with",
    isNull: "is null",
    isNotNull: "is not null",
    regex: "matches regex",
  };

  const previewDisplayValue = getTargetPreviewDisplayValue(
    url.matchBy,
    domain,
    url.value
  );

  return (
    <IgPaper
      sx={{
        backgroundColor: "#FAFAFA",
        borderRadius: "5px",
        boxShadow: "none",
        padding: "20px",
        marginBottom: "15px",
      }}
    >
      <Grid container direction={"column"}>
        <Grid
          container
          alignItems={"center"}
          justifyContent={"space-between"}
          key={index}
        >
          <Grid item>
            <IgSelect
              value={url.matchBy}
              onChange={(e) => onMatchChange(index, e.target.value)}
              variant={"outlined"}
              disabled={isArchived}
              MenuProps={{ disablePortal: true }}
              sx={{
                width: "205px",
                "& .Mui-disabled": {
                  backgroundColor: "#F5F5F5",
                },
              }}
            >
              {UrlMatchList.filter(
                (match) => match !== "isNull" && match !== "isNotNull"
              ).map((match) => (
                <MenuItem key={`filter-option-${match}`} value={match}>
                  {`URL ${urlLabels[match]}`}
                </MenuItem>
              ))}
            </IgSelect>
          </Grid>
          {!isArchived && (
            <Grid item>
              <IgIconButton onClick={() => onRemove(index)}>
                <Trash2
                  size={18}
                  className="trash-can"
                  color={theme.igIconColors.error}
                />
              </IgIconButton>
            </Grid>
          )}
        </Grid>
        {url.value.startsWith("/") && url.matchBy === "contains" && (
          <Grid item marginTop={"15px"}>
            <Alert
              severity={"warning"}
              sx={{
                border: `1px solid ${theme.igBackgroundColors.tonal.warning}`,
                borderRadius: "5px",
                alignItems: "center",
              }}
            >
              URL matching happens on the page path after the initial slash, so
              you should generally not have a leading slash.{" "}
              <IgLink
                onClick={() =>
                  openNewTab(
                    "https://docs.intelligems.io/analytics/custom-events/scoping-to-specific-pages"
                  )
                }
              >
                Learn more
              </IgLink>
              .
            </Alert>
          </Grid>
        )}
        <Grid item container marginTop={"15px"} direction={"column"}>
          <Grid item>
            <IgTextField
              placeholder={
                url.matchBy === "regex"
                  ? ".*collections/products"
                  : "pages/mypages"
              }
              disabled={isArchived}
              sx={{
                backgroundColor: "white",
                width: "100%",
                "& .Mui-disabled": {
                  backgroundColor: "#F5F5F5",
                },
              }}
              value={url.value}
              onChange={(e) => onUrlChange(index, e.target.value)}
            />
          </Grid>
          {url.matchBy === "regex" && error ? (
            <Grid item marginTop={"10px"}>
              <IgTypography sx={{ color: theme.palette.text.tonal.error }}>
                {error}
              </IgTypography>
            </Grid>
          ) : (
            <TargetPreview
              previewDisplayValue={previewDisplayValue}
              domain={domain}
              url={url}
            />
          )}
        </Grid>
      </Grid>
    </IgPaper>
  );
};
